/**
 * Pure presentation helpers for the in-editor voice-leading connector.
 *
 * Turns a `VoiceLeadingDiff` (Bowie's pitch-class minimal-motion model) into:
 *   - the stacked SVG lines the glyph draws (`buildConnectorLines`),
 *   - the card's one-line pair summary (`buildPairSummary`),
 *   - the button `aria-label` (`buildConnectorAria`),
 *   - the lexicon ids the reveal card shows (`cardLexiconIds`).
 *
 * Copy is Joni-locked (visual spec §6) and Bowie-locked on the honesty rules
 * (model spec §1/§2): the summary describes "the smoothest way these chords
 * connect," names added/dropped voices by PITCH never by role, and never says
 * "your voicing."
 *
 * Spec: docs/superpowers/specs/2026-06-06-voice-leading-diff-visual-spec.md
 */

import type { NoteName } from '@tunasong/schemas'
import type { VoiceDirection, VoiceLeadingDiff, VoiceMove } from '@tunasong/music-lib'
import { spokenText } from '../theory-copy/spoken-text.js'

/** A single drawn line in the stacked glyph. `added`/`dropped` carry no direction. */
export interface ConnectorLine {
  kind: 'held' | 'step' | 'leap' | 'added' | 'dropped'
  direction?: VoiceDirection
}

/** Maps a line kind to its CSS-module class key (reused from the Lab grammar). */
export const lineClassKey = (kind: ConnectorLine['kind']): 'commonToneHeld' | 'step' | 'leap' => {
  if (kind === 'held') {
    return 'commonToneHeld'
  }
  if (kind === 'leap') {
    return 'leap'
  }
  // step / added / dropped all render as the dotted "step" stroke; added/dropped
  // are further distinguished by an open endpoint glyph, not by stroke.
  return 'step'
}

/** Transform a diff into the stacked lines, top-down: held first, then moves, then added/dropped. */
export const buildConnectorLines = (diff: VoiceLeadingDiff): ConnectorLine[] => {
  const held: ConnectorLine[] = Array.from({ length: diff.heldCount }, () => ({ kind: 'held' }))
  const moves: ConnectorLine[] = diff.moves.map(m => ({ kind: m.motion, direction: m.direction }))
  const added: ConnectorLine[] = (diff.added ?? []).map(() => ({ kind: 'added' }))
  const dropped: ConnectorLine[] = (diff.dropped ?? []).map(() => ({ kind: 'dropped' }))
  return [...held, ...moves, ...added, ...dropped]
}

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six'] as const

const numberWord = (n: number): string => NUMBER_WORDS[n] ?? String(n)

const capitalise = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)

const pluralise = (word: string, n: number): string => (n === 1 ? word : `${word}s`)

const SMOOTHNESS_WORD: Record<VoiceLeadingDiff['smoothness'], string> = {
  smooth: 'Smooth',
  mixed: 'Mixed',
  angular: 'Angular',
}

interface MoveGroup {
  motion: VoiceMove['motion']
  direction: VoiceDirection
  count: number
}

/** Group moves by (motion, direction) preserving first-seen order for stable copy. */
const groupMoves = (moves: VoiceMove[]): MoveGroup[] => {
  const groups: MoveGroup[] = []
  for (const move of moves) {
    const existing = groups.find(g => g.motion === move.motion && g.direction === move.direction)
    if (existing) {
      existing.count += 1
    } else {
      groups.push({ motion: move.motion, direction: move.direction, count: 1 })
    }
  }
  return groups
}

/** "two steps up", "one leap down" — count word + pluralised motion noun + direction. */
const describeGroup = (group: MoveGroup): string =>
  `${numberWord(group.count)} ${pluralise(group.motion, group.count)} ${group.direction}`

/**
 * Build the card / aria pair-summary line from the bucket vocabulary
 * (visual spec §6). `transposeNote` maps each model pitch class to its
 * transposed spelling for added/dropped voice names; pass identity when the
 * chip is untransposed.
 */
export const buildPairSummary = (diff: VoiceLeadingDiff, transposeNote: (note: NoteName) => NoteName): string => {
  const clauses: string[] = []

  // Held clause (or the explicit "no shared notes" when none).
  if (diff.heldCount > 0) {
    clauses.push(`${numberWord(diff.heldCount)} common ${pluralise('tone', diff.heldCount)} held`)
  } else {
    clauses.push('no shared notes')
  }

  // Moves, grouped by motion + direction.
  const moveClauses = groupMoves(diff.moves).map(describeGroup)
  if (moveClauses.length > 0) {
    clauses.push(moveClauses.join(', '))
  }

  // Added / dropped voices, named by PITCH (Bowie: never by role).
  for (const note of diff.added ?? []) {
    clauses.push(`${transposeNote(note)} joins`)
  }
  for (const note of diff.dropped ?? []) {
    clauses.push(`${transposeNote(note)} drops away`)
  }

  const body = capitalise(clauses.join('; '))
  return `${body}. ${SMOOTHNESS_WORD[diff.smoothness]}.`
}

/**
 * Full button aria-label: the chord-pair transition, then the bucket summary.
 * Note names run through `spokenText` so ♭/♯ are spoken. `prevName`/`currName`
 * are the already-transposed display names from the chip.
 */
export const buildConnectorAria = (
  diff: VoiceLeadingDiff,
  prevName: string,
  currName: string,
  transposeNote: (note: NoteName) => NoteName
): string => spokenText(`Voice motion, ${prevName} to ${currName}. ${buildPairSummary(diff, transposeNote)}`)

/**
 * Lexicon ids for the reveal card: the surface intro always, then the concepts
 * present in this pair — common-tone if any held, stepwise if any step, leap if
 * any leap. Held/step reuse the Lab entries; leap is the new neutral concept.
 */
export const cardLexiconIds = (diff: VoiceLeadingDiff): string[] => {
  const ids = ['voiceleading-diff']
  if (diff.heldCount > 0) {
    ids.push('vl-common-tone')
  }
  if (diff.moves.some(m => m.motion === 'step')) {
    ids.push('vl-stepwise')
  }
  if (diff.moves.some(m => m.motion === 'leap')) {
    ids.push('voiceleading-leap')
  }
  return ids
}
